import { useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';

const useDragAndDrop = ({ acceptedFileTypes, handleFileSelect }) => {
  const [isDragging, setIsDragging] = useState(false);
  const toast = useToast();

  const validateDroppedFile = useCallback(
    (file) => {
      if (!file) return { isValid: false, error: 'No file provided' };

      // Check file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const isValidType = acceptedFileTypes.some((type) =>
        type.toLowerCase().includes(fileExtension)
      );

      if (!isValidType) {
        return {
          isValid: false,
          error: `Invalid file type. Accepted formats are: ${acceptedFileTypes.join(', ')}`
        };
      }

      return { isValid: true, error: null };
    },
    [acceptedFileTypes]
  );

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const files = event.dataTransfer.files;

      // Check if multiple files were dropped
      if (files.length > 1) {
        toast({
          title: 'Multiple Files Detected',
          description: 'Please drop only one file at a time.',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      // Check if no files were dropped
      if (files.length === 0) {
        toast({
          title: 'No File Detected',
          description: 'Please drop a valid file.',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      const file = files[0];
      const validation = validateDroppedFile(file);

      if (!validation.isValid) {
        toast({
          title: 'Invalid File',
          description: validation.error,
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      // Create a synthetic event to match the expected format of handleFileSelect
      const syntheticEvent = {
        target: {
          files: [file]
        }
      };

      handleFileSelect(syntheticEvent);
    },
    [validateDroppedFile, handleFileSelect, toast]
  );

  return {
    isDragging,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop
  };
};

export { useDragAndDrop };
